import { Col, Row, Spin, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import "./terms-of-service.scss";

type TermsOfServiceProps = {
  allData: any;
  tab: string;
  loading: boolean;
};

const TermsOfService = ({ allData, tab, loading }: TermsOfServiceProps) => {
  const [activeId, setActiveId] = useState("");
  const [listData, setListData] = useState<any>([]);

  // TOC
  const Headings = ({ headings, activeId }: any) => (
    <ul className="heading-ul">
      {headings.map((heading: any) => (
        <li key={heading.id} className="heading-li">
          <a
            className={`heading-title ${heading.id === activeId ? "active" : ""}`}
            href={`#${heading.id}`}
            onClick={(e) => {
              e.preventDefault();
              setActiveId(heading.id);
              document.querySelector(`#${heading.id}`)?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          >
            {heading.name}
          </a>
        </li>
      ))}
    </ul>
  );

  const getNestedHeadings = (headingElements: any) => {
    const nestedHeadings: any[] = [];

    headingElements.forEach((heading: any, index: number) => {
      const { innerText: name, id } = heading;

      if (heading.nodeName === "H1" && heading.id.startsWith("terms-of-service-name")) {
        nestedHeadings.push({ id, name });
      }
    });

    return nestedHeadings;
  };

  const useHeadingsData = () => {
    const [nestedHeadings, setNestedHeadings] = useState([]);

    useEffect(() => {
      setTimeout(() => {
        const headingElements = Array.from(
          document.querySelectorAll('h1[id^="terms-of-service-name"]')
        );
        const newNestedHeadings: any = getNestedHeadings(headingElements);
        setNestedHeadings(newNestedHeadings);
      }, 333);
    }, [listData]);

    return { nestedHeadings };
  };

  const TableOfContents = () => {
    useIntersectionObserver(setActiveId, activeId);
    const { nestedHeadings } = useHeadingsData();
    return (
      <div aria-label="List of policies">
        {loading ? (
          <Spin size="default" />
        ) : (
          <Headings headings={nestedHeadings} activeId={activeId} />
        )}
      </div>
    );
  };

  // Intersection Observer
  const useIntersectionObserver = (setActiveId: any, activeId: string) => {
    const headingElementRef = useRef<any>({});

    useEffect(() => {
      const callback = (headings: any) => {
        headingElementRef.current = headings.reduce(
          (map: any, headingElement: any) => {
            map[headingElement.target.id] = headingElement;
            return map;
          },
          headingElementRef.current
        );

        const visibleHeadings: any[] = [];
        Object.keys(headingElementRef.current).forEach((key) => {
          const headingElement = headingElementRef.current[key];
          if (headingElement.isIntersecting)
            visibleHeadings.push(headingElement);
        });

        const getIndexFromId = (id: string) =>
          headingElements.findIndex((heading) => heading.id === id);

        if (visibleHeadings.length === 0) {
          const activeElement = headingElements.find(
            (el) => el.id === activeId
          );
          const activeIndex = headingElements.findIndex(
            (el) => el.id === activeId
          );

          const activeIdYcoord = activeElement?.getBoundingClientRect().y;
          if (activeIdYcoord && activeIdYcoord > 150 && activeIndex !== 0) {
            setActiveId(headingElements[activeIndex - 1].id);
          }
        } else if (visibleHeadings.length === 1) {
          setActiveId(visibleHeadings[0].target.id);
        } else if (visibleHeadings.length > 1) {
          const sortedVisibleHeadings = [...visibleHeadings].sort(
            (a, b) => getIndexFromId(a.target.id) - getIndexFromId(b.target.id)
          );
          setActiveId(sortedVisibleHeadings[1].target.id);
        }
      };

      const observer = new IntersectionObserver(callback, {
        rootMargin: "-110px 0px -40% 0px",
      });

      const headingElements = Array.from(document.querySelectorAll('h1[id^="terms-of-service-name"]'));
      headingElements.forEach((element) => observer.observe(element));

      return () => observer.disconnect();
    }, [listData, activeId]);
  };

  // useEffect
  useEffect(() => {
    if (allData) {
      setListData(allData);
    }
  }, [allData]);

  useEffect(() => {
    if (tab === "2") {
      setActiveId(`terms-of-service-name-0`);
    }
  }, [tab]);

  return (
    <Row className="terms-of-service-row">
      <Col
        className="terms-of-service-left-col"
        id="terms-of-service-left-col"
        xl={{ span: 6 }}
        md={{ span: 6 }}
        sm={{ span: 24 }}
        xs={{ span: 24 }}
      >
        <div className={`left-container`}>{TableOfContents()}</div>
      </Col>
      <Col
        className="terms-of-service-right-col"
        id={`terms-of-service-right-col`}
        xl={{ span: 18 }}
        md={{ span: 18 }}
        sm={{ span: 24 }}
        xs={{ span: 24 }}
      >
        <div className={`right-container`} id={`right-container`}>
          <Typography.Title level={5} style={{ padding: 30, color: "#929396" }}>
            ĐIỀU KHOẢN DỊCH VỤ CỦA CHÚNG TÔI
          </Typography.Title>

          {loading ? (
            <Spin size="default" />
          ) : (
            listData.map((item: any, index: number) => {
              return (
                <div key={item.id} className="card-item">
                  <h1
                    id={`terms-of-service-name-${index}`}
                    className="terms-of-service-content"
                    style={{ marginBottom: 30, fontSize: 21 }}
                  >
                    {item?.name}
                  </h1>
                  <div
                    className="ql-container ql-snow"
                    style={{ border: "none" }}
                  >
                    <div className="ql-editor">
                      <div
                        dangerouslySetInnerHTML={{ __html: item?.description }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Col>
    </Row>
  );
};

export default TermsOfService;
